import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Upload, Trash2, FileText, Image as ImageIcon, Download, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { UnitLinkSelect } from '../job/UnitLinkSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (ct = '') => {
  if (ct.startsWith('image/')) return ImageIcon;
  return FileText;
};

export default function FieldEventAttachments({ eventId, canEdit, dark, pendingFiles, onPendingChange, equipment = [] }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  // "Pending" mode: no event exists yet (Create Event), so files are staged
  // locally and uploaded by the dialog once the event has been created.
  const pending = !eventId && !!onPendingChange;

  const load = useCallback(async () => {
    if (!eventId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/field/events/${eventId}/files`);
      setAttachments(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (pending) {
      onPendingChange([...(pendingFiles || []), ...files]);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/field/events/${eventId}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachments((prev) => [...prev, res.data]);
      }
      toast.success(files.length === 1 ? 'File uploaded' : `${files.length} files uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAttachment = async (id) => {
    if (pending) {
      onPendingChange((pendingFiles || []).filter((_, i) => `pending-${i}` !== id));
      return;
    }
    try {
      await axios.delete(`${API}/field/events/${eventId}/files/${id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      toast.success('Attachment removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const linkUnit = async (attachment, equipment_item_id) => {
    try {
      const r = await axios.patch(`${API}/field/events/${eventId}/files/${attachment.id}/unit`, { equipment_item_id });
      setAttachments((rows) => rows.map((a) => a.id === attachment.id ? { ...a, ...r.data } : a));
      toast.success(equipment_item_id ? 'File linked to asset' : 'Asset link removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Unable to link file. Save equipment changes first.'); }
  };

  const openAttachment = async (att) => {
    setOpeningId(att.id);
    try {
      const url = att.url.startsWith('http') ? att.url : `${process.env.REACT_APP_BACKEND_URL}${att.url}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: att.content_type || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');
      if (!win) {
        // Popup blocked — fall back to a download.
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = att.filename;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open file');
    } finally {
      setOpeningId(null);
    }
  };

  const rowClass = dark
    ? 'flex w-full min-w-0 items-center gap-2 px-2 py-1.5 rounded-md border border-slate-700 bg-slate-800/50 text-sm'
    : 'flex w-full min-w-0 items-center gap-2 px-2 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm';

  const onDragEnter = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) setIsDragging(true);
  };
  const onDragOver = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    // Ignore leaves onto internal children.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };
  const onDrop = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length) upload(files);
  };

  const rows = pending
    ? (pendingFiles || []).map((f, i) => ({
        id: `pending-${i}`, filename: f.name, size: f.size, content_type: f.type, staged: true,
      }))
    : attachments;

  return (
    <div
      data-testid="field-event-attachments"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative min-w-0 max-w-full overflow-hidden rounded-md transition-colors ${
        isDragging
          ? dark
            ? 'ring-2 ring-sky-500 ring-inset bg-sky-950/40'
            : 'ring-2 ring-sky-500 ring-inset bg-sky-50'
          : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Paperclip size={14} /> Attachments ({rows.length})
        </div>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => upload(e.target.files)}
              data-testid="field-event-attachment-input"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="field-event-attachment-upload-btn"
              className={dark ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}
            >
              {uploading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Upload size={13} className="mr-1.5" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        )}
      </div>
      {loading ? (
        <p className="text-xs text-slate-500 dark:text-slate-400 py-1">Loading…</p>
      ) : rows.length === 0 ? (
        canEdit ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`w-full text-xs py-4 rounded-md border-2 border-dashed transition-colors ${
              dark
                ? 'border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700'
            }`}
            data-testid="field-event-attachment-dropzone"
          >
            Drop files here, or click to browse
          </button>
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400 py-1">No files attached yet.</p>
        )
      ) : (
        <div className="min-w-0 space-y-1.5">
          {rows.map((a) => {
            const Icon = iconFor(a.content_type);
            return (
              <div key={a.id} className={`${rowClass} flex-wrap`} data-testid={`field-event-attachment-${a.id}`}>
                <Icon size={14} className={dark ? 'text-slate-400 shrink-0' : 'text-slate-500 shrink-0'} />
                {a.staged ? (
                  <span className="flex-1 min-w-0 truncate" title={a.filename}>{a.filename}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openAttachment(a)}
                    className="flex-1 min-w-0 text-left truncate underline-offset-2 hover:underline"
                    title={a.filename}
                    data-testid={`field-event-attachment-open-${a.id}`}
                  >
                    {a.filename}
                  </button>
                )}
                <span className={`text-[11px] shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{fmtSize(a.size)}</span>
                {!a.staged && (
                <button
                  type="button"
                  onClick={() => openAttachment(a)}
                  disabled={openingId === a.id}
                  className={`p-1 rounded ${dark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200'}`}
                  title="Open"
                  data-testid={`field-event-attachment-download-${a.id}`}
                >
                  {openingId === a.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    className={`p-1 rounded ${dark ? 'text-red-400 hover:bg-red-950' : 'text-red-500 hover:bg-red-50'}`}
                    title="Remove"
                    data-testid={`field-event-attachment-remove-${a.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
                {!a.staged && canEdit && <div className="basis-full min-w-0"><UnitLinkSelect equipment={equipment} value={a.equipment_item_id || ''} onChange={(value) => linkUnit(a, value)} testid={`field-file-unit-${a.id}`} /></div>}
                {!canEdit && a.unit_ref && <p className="basis-full text-xs text-sky-700" data-testid={`field-file-unit-label-${a.id}`}>Asset {a.unit_ref.unit_id || a.unit_ref.serial_number}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
